import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import OrderAttentionPanel from '../../components/orders/OrderAttentionPanel';
import OrderHistory from '../../components/orders/OrderHistory';
import { ASSIGNMENT_STATUS, parcelStatusLabel } from '../../components/delivery/ParcelRecord';
import { useBusinessAuth } from '../../context/BusinessAuthContext';
import { businessFulfillmentService } from '../../services/businessFulfillmentService';
import { hasFulfillmentWork } from '../../utils/orderAttention';
import { orderProgress, paymentLabel } from '../../utils/ordersOperationsPresentation';
import { PageHeader } from '../../../components/ui';

// The whole order on one page: what was bought, how it was paid, where the
// parcel is, and everything that happened to it. Reading only. Work on an open
// order is done in Fulfillment, and this page links there when there is any.

const money = (value) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(Number(value || 0));
const dateTime = (value) => {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Not recorded' : new Intl.DateTimeFormat('en-PH', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
};
const panel = 'rounded-2xl border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-950';
const focusRing = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus';
const textLink = `font-semibold underline underline-offset-4 ${focusRing}`;

function Fact({ term, children }) {
  if (children == null || children === '') return null;
  return <div className="min-w-0"><dt className="text-xs text-neutral-600 dark:text-neutral-400">{term}</dt><dd className="mt-0.5 break-words text-sm font-medium">{children}</dd></div>;
}

export default function OrderRecordPage() {
  const { orderId } = useParams();
  const auth = useBusinessAuth();
  const owner = String(auth?.role || '').toUpperCase() === 'OWNER';
  const canReadParcels = owner || Boolean(auth?.hasPermission?.('delivery.dispatch.read.business'));
  const [order, setOrder] = useState(null);
  const [state, setState] = useState('loading');
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestRef.current;
    setState('loading');
    try {
      const next = await businessFulfillmentService.detail(orderId);
      if (requestId !== requestRef.current) return;
      setOrder(next); setState('ready');
    } catch (requestError) {
      if (requestId !== requestRef.current) return;
      setOrder(null);
      const status = requestError?.response?.status;
      setState(status === 404 ? 'missing' : status === 403 ? 'restricted' : 'error');
    }
  }, [orderId]);
  useEffect(() => { load(); return () => { requestRef.current += 1; }; }, [load]);

  const progress = order ? orderProgress({ shipmentStatus: order.shipmentStatus, sellerFulfillmentStatus: order.fulfillmentStatus }) : null;
  const itemTotal = (order?.items || []).reduce((sum, item) => sum + Number(item.price || 0) * Number(item.requiredQuantity || 0), 0);
  const delivery = order?.delivery || {};

  return (
    <main className="min-w-0 space-y-5 text-neutral-950 dark:text-white">
      <Link to="/business/orders" className={`inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-secondary hover:text-accent ${focusRing}`}><ArrowLeft className="h-4 w-4" aria-hidden="true" />Order records</Link>

      {state === 'loading' ? <div role="status" className="grid min-h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-accent motion-reduce:animate-none" aria-hidden="true" /><span className="sr-only">Loading the order record…</span></div> : null}
      {state === 'missing' ? <p role="alert" className="rounded-2xl border border-warning bg-warning-subtle p-4 text-sm text-warning">That order was not found. It may belong to another business, or the link may be wrong.</p> : null}
      {state === 'restricted' ? <p role="alert" className="rounded-2xl border border-warning bg-warning-subtle p-4 text-sm text-warning">You do not have access to order records. Ask the business owner if you need this access.</p> : null}
      {state === 'error' ? <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-warning bg-warning-subtle p-4 text-sm text-warning"><span>This order could not be loaded. Check your connection and try again.</span><button type="button" onClick={load} className="min-h-11 rounded-xl border border-warning px-4 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">Try again</button></div> : null}

      {state === 'ready' && order ? (
        <>
          <PageHeader
            title={order.orderReference}
            description={`Placed ${dateTime(order.placedAt)} · ${order.customer || 'Customer unavailable'}`}
            actions={<>
              <span className="inline-flex rounded-full bg-neutral-100 px-3 py-1 text-sm font-semibold dark:bg-white/10">{progress.label}</span>
              {hasFulfillmentWork(order) ? <Link to={`/business/orders/fulfillment/${order.orderId}`} className={`business-primary-action inline-flex min-h-11 items-center rounded-xl bg-accent px-4 text-sm font-semibold text-on-accent hover:bg-accent-hover active:bg-accent-active focus-visible:ring-offset-2 ${focusRing}`}>Continue in Fulfillment</Link> : null}
            </>}
          />

          <OrderAttentionPanel order={order} />

          <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
            <div className="space-y-5">
              <section aria-labelledby="order-items-title" className={`overflow-hidden ${panel}`}>
                <h2 id="order-items-title" className="border-b border-neutral-200 px-4 py-3 font-semibold dark:border-neutral-800">Items</h2>
                {(order.items || []).length ? (
                  <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
                    {order.items.map((item) => (
                      <li key={item.orderItemId} className="flex items-start justify-between gap-4 px-4 py-3 text-sm">
                        <div className="min-w-0"><p className="break-words font-medium">{item.name}</p><p className="text-neutral-600 dark:text-neutral-300">{item.variation || 'Standard variant'} · {item.packedQuantity} of {item.requiredQuantity} packed</p></div>
                        <p className="shrink-0 text-right tabular-nums"><span className="block font-medium">{money(Number(item.price) * Number(item.requiredQuantity))}</span><span className="text-xs text-neutral-600 dark:text-neutral-400">{item.requiredQuantity} × {money(item.price)}</span></p>
                      </li>
                    ))}
                  </ul>
                ) : <p className="px-4 py-4 text-sm text-neutral-600 dark:text-neutral-300">No items are recorded on this order.</p>}
                <dl className="grid gap-3 border-t border-neutral-200 px-4 py-3 sm:grid-cols-3 dark:border-neutral-800">
                  <Fact term="Items subtotal">{money(itemTotal)}</Fact>
                  <Fact term="Order total">{money(order.total)}</Fact>
                  <Fact term="Payment">{order.payment ? paymentLabel(order.payment.method, order.payment.status) : 'Payment unavailable'}</Fact>
                </dl>
              </section>

              <section aria-labelledby="order-delivery-title" className={`p-4 ${panel}`}>
                <h2 id="order-delivery-title" className="font-semibold">Delivery</h2>
                <dl className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2">
                  <Fact term="Parcel">{`Parcel #${order.shipmentId} · ${parcelStatusLabel(order.shipmentStatus)}`}</Fact>
                  <Fact term="Label">{order.parcelLabel?.code ? `${order.parcelLabel.code}${order.parcelLabel.attachedAt ? ', on the parcel' : ', not yet confirmed on the parcel'}` : 'No label generated'}</Fact>
                  <Fact term="Rider">{delivery.riderName ? `${delivery.riderName} · ${ASSIGNMENT_STATUS[delivery.assignmentStatus] || 'Assigned'}` : 'No rider assigned'}</Fact>
                  <Fact term="Trip">{delivery.runId ? `Trip #${delivery.runId}` : 'Not on a trip'}</Fact>
                  <Fact term="Recipient">{[order.shipTo?.name, order.shipTo?.phone].filter(Boolean).join(' · ')}</Fact>
                  <Fact term="Delivery address">{order.deliveryAddress || 'Not recorded'}</Fact>
                </dl>
                {order.customerNote ? <div className="mt-3 border-t border-neutral-200 pt-3 dark:border-neutral-800"><p className="text-xs text-neutral-600 dark:text-neutral-400">Shopper note</p><p className="mt-0.5 break-words text-sm">{order.customerNote}</p></div> : null}
                {canReadParcels ? (
                  <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 border-t border-neutral-200 pt-3 text-sm dark:border-neutral-800">
                    <li><Link to={`/business/delivery?view=parcels&parcel=${order.shipmentId}`} className={`inline-flex min-h-9 items-center ${textLink}`}>Parcel record</Link></li>
                    {delivery.runId ? <li><Link to={`/business/delivery?view=trips&trip=${delivery.runId}`} className={`inline-flex min-h-9 items-center ${textLink}`}>Trip #{delivery.runId}</Link></li> : null}
                  </ul>
                ) : null}
              </section>
            </div>

            <section className={`p-4 ${panel}`}>
              <OrderHistory timeline={order.timeline} headingId="order-record-history" title="History" />
            </section>
          </div>
        </>
      ) : null}
    </main>
  );
}
